import React, { useState } from "react";
import Collapse from "../../../components/Collapse";
import { formatBook } from "../../../utils/formatOdds";

/**
 * 1 Day Dragon Tiger bet table (dt6 / pdt6 / tdt6) in the reference admin's markup —
 * rendered inside the page's `.casino-detail`:
 *   .dt1dayfancy (Dragon back/lay | Pair back | Tiger back/lay, each with a range popup + book)
 *   + .teen1daycasino-container (Dragon | Tiger: odds row, Even/Odd/Black/Red row, range,
 *     suit icons row, Spade/Heart/Club/Diamond row, range)
 *   + .casino-remark (only when the feed has a non-empty remark)
 * Styled by the ported reference rules in styles/index.css (+ styles/games/dt6.css).
 *
 * Markets are read like the reference: main = sub[subtype dt6] by sr (0 Dragon, 1 Tiger),
 * fancy = sub[subtype dt6fancy] by sr (0 Pair, 1-4 Dragon Even/Odd/Red/Black,
 * 5-8 Dragon Spade/Heart/Diamond/Club, 9-12 Tiger Even/Odd/Red/Black, 13-16 Tiger suits),
 * with a lookup by `nat` as fallback.
 */
const SUIT_IMG = `${import.meta.env.BASE_URL}img/cardIcons`;
const REMARK_ICON = `${import.meta.env.BASE_URL}assets/img/icons/remark.png`;

// Reference v-kvalmin / v-kvalmax: 1000 → "1K", 200000 → "2L" (2 decimals, trailing zeros dropped)
function formatRangeVal(val) {
  const n = Number(val);
  if (val == null || val === "" || Number.isNaN(n)) return "";
  if (n >= 100000) return `${parseFloat((n / 100000).toFixed(2))}L`;
  if (n >= 1000) return `${parseFloat((n / 1000).toFixed(2))}K`;
  return String(parseFloat(n.toFixed(2)));
}

const bySr = (a, b) => (Number(a?.sr) || 0) - (Number(b?.sr) || 0);

const FANCY_NAMES = [
  "Pair",
  "Dragon Even", "Dragon Odd", "Dragon Red", "Dragon Black",
  "Dragon Spade", "Dragon Heart", "Dragon Diamond", "Dragon Club",
  "Tiger Even", "Tiger Odd", "Tiger Red", "Tiger Black",
  "Tiger Spade", "Tiger Heart", "Tiger Diamond", "Tiger Club",
];

export default function BetTableDragon6({ data = [], onBetClick, exposures = {}, remark }) {
  // Range popups (#demo0..#demo2), toggled like the reference's bootstrap collapse
  const [openRange, setOpenRange] = useState({});
  const toggleRange = (id) => setOpenRange((s) => ({ ...s, [id]: !s[id] }));

  const list = Array.isArray(data) ? data : [];
  const byName = (name) => list.find((item) => item?.nat === name);
  const ofType = (re) => list.filter((s) => re.test(String(s?.subtype || ""))).sort(bySr);
  const mainArr = ofType(/^[a-z]*dt6$/);
  const fancyArr = ofType(/^[a-z]*dt6fancy$/);
  const main = (i) => byName(i === 0 ? "Dragon" : "Tiger") || mainArr[i] || { nat: i === 0 ? "Dragon" : "Tiger" };
  const fancy = (i) => byName(FANCY_NAMES[i]) || fancyArr[i] || { nat: FANCY_NAMES[i] };

  const isSuspended = (b) => String(b?.gstatus || "").toUpperCase() !== "OPEN";
  const val = (v) => (v !== undefined && v !== null && v !== "" ? v : 0);
  const getExposure = (name) => {
    const v = exposures[name] ?? exposures[name?.toLowerCase?.()];
    return v != null ? Number(v) || 0 : 0;
  };
  // Reference v-casinobook / v-casinofancybook: amount > 0 → book-green, otherwise book-red
  const bookClass = (exp) => (exp > 0 ? "book-green" : "book-red");
  const click = (b, odd, type) => {
    if (isSuspended(b)) return;
    onBetClick?.(b?.nat, odd, b, type);
  };

  const rangeIcon = (id, b) => (
    <div className="float-left mr-2">
      <i
        data-toggle="collapse"
        data-target={`#${id}`}
        className="fas fa-info-circle"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleRange(id);
        }}
      />{" "}
      <Collapse id={id} open={!!openRange[id]} className="icon-range">
        {"\n                                            R:"}
        <span>{formatRangeVal(b?.min)}</span>-<span>{formatRangeVal(b?.max)}</span>
      </Collapse>
    </div>
  );

  const fancyRow = (cls, id, b, withLay) => {
    const exp = getExposure(b?.nat);
    const susp = isSuspended(b) ? " suspended" : "";
    return (
      <div className={`casino-box-row ${cls}`}>
        <div className="casino-nation-name">
          {rangeIcon(id, b)} <b>{b?.nat}</b>
        </div>{" "}
        <div className="casino-bl-box">
          <div className={`back casino-bl-box-item${susp}`} onClick={() => click(b, val(b?.b), "back")}>
            <span className="casino-box-odd">{val(b?.b)}</span>
          </div>
          {withLay && (
            <>
              {" "}
              <div className={`lay casino-bl-box-item${susp}`} onClick={() => click(b, val(b?.l), "lay")}>
                <span className="casino-box-odd">{val(b?.l)}</span>
              </div>
            </>
          )}
        </div>{" "}
        <div className="casino-nation-name text-center w-100">
          <span className={`casino-book ${bookClass(exp)}`}>{formatBook(exp)}</span>
        </div>
      </div>
    );
  };

  const range = (b) => (
    <div className="casino-box-row casino-odds">
      <div className="text-center w-100">
        <span className="float-right casino-min-max">
          R:<span>{formatRangeVal(b?.min)}</span>-<span>{formatRangeVal(b?.max)}</span>
        </span>
      </div>
    </div>
  );

  // Even / Odd / Black / Red boxes: .suspended on the .casino-bl-box wrapper (reference)
  const eoBox = (b, content, img) => {
    const exp = getExposure(b?.nat);
    return (
      <div className={`casino-bl-box${isSuspended(b) ? " suspended" : ""}`}>
        <div
          className={`back casino-bl-box-item${img ? " casino-card-img" : ""}`}
          onClick={() => click(b, val(b?.b), "back")}
        >
          {content}{" "}
          <span className={`casino-book ${bookClass(exp)}`}>{formatBook(exp)}</span>
        </div>
      </div>
    );
  };

  // Suit boxes: .suspended on the item; the book directive adds .lock-top to a suspended parent
  const suitBox = (b) => {
    const exp = getExposure(b?.nat);
    const susp = isSuspended(b);
    return (
      <div className="casino-bl-box">
        <div
          className={`back casino-bl-box-item${susp ? " suspended lock-top" : ""}`}
          onClick={() => click(b, val(b?.b), "back")}
        >
          <span className="casino-box-odd">{val(b?.b)}</span>{" "}
          <span className={`casino-book ${bookClass(exp)}`}>{formatBook(exp)}</span>
        </div>
      </div>
    );
  };

  const suitIcon = (s) => (
    <div className="casino-bl-box">
      <div className="casino-bl-box-item casino-card-img">
        <img src={`${SUIT_IMG}/${s}.png`} alt="" />
      </div>
    </div>
  );

  const pair = (a, b) => (
    <span>
      <img src={`${SUIT_IMG}/${a}.png`} alt="" /> <img src={`${SUIT_IMG}/${b}.png`} alt="" />
    </span>
  );

  // off = 1 (Dragon) / 9 (Tiger): Even, Odd, Red, Black, Spade, Heart, Diamond, Club
  const side = (name, off) => {
    const even = fancy(off);
    const odd = fancy(off + 1);
    const red = fancy(off + 2);
    const black = fancy(off + 3);
    const isDragon = name === "Dragon";
    return (
      <div>
        <div className="casino-box-row casino-odds casino-title">
          <div className="text-left w-100">
            <b className={isDragon ? "text-playera" : "text-playerb"}>{name}</b>
          </div>
        </div>{" "}
        <div className="casino-box-row">
          <div className="casino-bl-box">
            <b>{val(even?.b)}</b>
          </div>{" "}
          <div className="casino-bl-box">
            <b>{val(odd?.b)}</b>
          </div>{" "}
          <div className="casino-bl-box">
            <b>{val(red?.b)}</b>
          </div>{" "}
          <div className="casino-bl-box">
            <b>{val(black?.b)}</b>
          </div>
        </div>{" "}
        <div className="casino-box-row">
          {eoBox(even, <span className="casino-box-odd">Even</span>)}{" "}
          {eoBox(odd, <span className="casino-box-odd">Odd</span>)}{" "}
          {isDragon ? eoBox(black, pair("spade", "club"), true) : eoBox(red, pair("heart", "diamond"), true)}{" "}
          {isDragon ? eoBox(red, pair("heart", "diamond"), true) : eoBox(black, pair("spade", "club"), true)}
        </div>{" "}
        {range(even)}
      </div>
    );
  };

  const suits = (off) => (
    <div className="mt-3">
      <div className="casino-box-row">
        {suitIcon("spade")} {suitIcon("heart")} {suitIcon("club")} {suitIcon("diamond")}
      </div>{" "}
      <div className="casino-box-row">
        {suitBox(fancy(off + 4))} {suitBox(fancy(off + 5))} {suitBox(fancy(off + 7))} {suitBox(fancy(off + 6))}
      </div>{" "}
      {range(fancy(off + 4))}
    </div>
  );

  return (
    <>
      <div className="dt1dayfancy">
        {fancyRow("dragonfancy", "demo0", main(0), true)}{" "}
        {fancyRow("pairfancy", "demo2", fancy(0), false)}{" "}
        {fancyRow("tigerfancy", "demo1", main(1), true)}
      </div>{" "}
      <div className="teen1daycasino-container mt-2">
        <div className="teen1dayleft">
          {side("Dragon", 1)} {suits(1)}
        </div>{" "}
        <div className="teen1daycenter" />{" "}
        <div className="teen1dayright">
          {side("Tiger", 9)} {suits(9)}
        </div>
      </div>{" "}
      {remark != null && remark !== "" && (
        <div className="casino-remark mt-3">
          <div className="remark-icon">
            <img src={REMARK_ICON} alt="" />
          </div>{" "}
          {React.createElement("marquee", null, remark)}
        </div>
      )}
    </>
  );
}
